/**
 * WS health beacon — Sentry-independent, CloudWatch-queryable telemetry for
 * Y.js WebSocket wedge conditions.
 *
 * The chandler+marketing@rivereditor.com incident had ZERO Sentry events for
 * the affected population: the tab's org-room WS layer died silently for
 * 21+ hours while REST/SSE chat kept working normally, masking the outage
 * entirely from every alert that depended on an exception firing. This rides
 * the existing analytics pipe instead — every event lands in
 * `logs/analytics.log` (shipped to CloudWatch in prod) via the always-on
 * `LogProvider`, independent of Sentry sampling/quota/availability.
 *
 * Single subscription point for the four signals the Rock-Solid WebSocket
 * Recovery plan calls for:
 *   (a) never_synced        — wsObservability.ts, provider never reached
 *                             first sync within 30s of open
 *   (b) cumulative_downtime — wsObservability.ts, cumulative not-synced time
 *                             crosses the prolonged-disconnect threshold
 *                             while the tab is visible
 *   (c) hard_stop           — wired here directly via wsHardStop's
 *                             onHardStop(), no other module needs to know
 *                             this beacon exists
 *   (d) zombie_recovery     — wsReconnectCoordinator.ts, a connected-zombie
 *                             or stuck-connecting provider was force-reconnected
 *
 * See docs/Operations/Analytics/ANALYTICS_SYSTEM.md ("WS Health Beacon").
 */
import { analytics } from '../analytics/analytics';
import { WEBSOCKET_URL } from '../../api/config';
import { onHardStop, type HardStopEvent } from './wsHardStop';

interface EmitOptions {
	roomPrefix: string;
	downtimeMs?: number;
	wedgeKind?: 'connected_never_synced' | 'stuck_connecting';
	closeCode?: number;
	closeReason?: string;
}

function emit(
	issueType: 'never_synced' | 'cumulative_downtime' | 'hard_stop' | 'zombie_recovery',
	opts: EmitOptions,
): void {
	try {
		analytics.track('ws_health_issue', {
			issue_type: issueType,
			room_prefix: opts.roomPrefix,
			websocket_url: WEBSOCKET_URL ?? '(unresolved)',
			...(opts.downtimeMs !== undefined ? { downtime_ms: opts.downtimeMs } : {}),
			...(opts.wedgeKind ? { wedge_kind: opts.wedgeKind } : {}),
			...(opts.closeCode !== undefined ? { close_code: opts.closeCode } : {}),
			...(opts.closeReason ? { close_reason: opts.closeReason } : {}),
		});
	} catch {
		// Telemetry must never block real WS work.
	}
}

/** A room's provider never reached its first sync within the observation window. */
export function reportNeverSynced(roomPrefix: string, elapsedMs: number): void {
	emit('never_synced', { roomPrefix, downtimeMs: elapsedMs });
}

/** Cumulative not-synced time for a room crossed the prolonged-disconnect threshold while visible. */
export function reportCumulativeDowntime(roomPrefix: string, downtimeMs: number): void {
	emit('cumulative_downtime', { roomPrefix, downtimeMs });
}

/** A wedged (connected-zombie or stuck-connecting) provider was force-reconnected. */
export function reportZombieRecovery(
	roomPrefix: string,
	wedgeKind: 'connected_never_synced' | 'stuck_connecting',
): void {
	emit('zombie_recovery', { roomPrefix, wedgeKind });
}

// Hard-stop is wired here directly, once, globally — wsHardStop.ts stays
// telemetry-agnostic (it already broadcasts to onHardStop subscribers for
// exactly this purpose, e.g. a UI banner).
onHardStop((event: HardStopEvent) => {
	emit('hard_stop', {
		roomPrefix: event.roomShort,
		closeCode: event.trigger.code,
		closeReason: event.trigger.reason ?? event.cause,
	});
});
